/**
 * BulkActionsBar Component
 * Toolbar for bulk file operations (move, copy, delete)
 */

import { html, attr, on, Signal } from '@tempots/dom'
import { Button, ButtonVariant, Icon } from '@tempots/beatui'
import { ConfirmDialog } from '@/components/ui'

// ============================================================================
// Types
// ============================================================================

export interface BulkActionsBarProps {
  /** Number of selected files */
  selectedCount: Signal<number>
  /** Whether a bulk action is in progress */
  performingAction: Signal<boolean>
  /** Callback to clear the selection */
  onClearSelection: () => void
  /** Callback for move action */
  onMove: () => void
  /** Callback for copy action */
  onCopy: () => void
  /** Callback for delete action */
  onDelete: () => Promise<void>
}

// ============================================================================
// Component
// ============================================================================

/**
 * BulkActionsBar - Toolbar for bulk file operations
 *
 * @example
 * ```typescript
 * When(hasSelectedFiles, () =>
 *   BulkActionsBar({
 *     selectedCount,
 *     performingAction: performingBulkAction,
 *     onClearSelection: clearSelection,
 *     onMove: openBulkMoveModal,
 *     onCopy: openBulkCopyModal,
 *     onDelete: handleBulkDelete,
 *   })
 * )
 * ```
 */
export function BulkActionsBar({
  selectedCount,
  performingAction,
  onClearSelection,
  onMove,
  onCopy,
  onDelete,
}: BulkActionsBarProps) {
  return html.div(
    attr.class(
      'bg-brand-50 border border-brand-200 rounded-lg p-4 flex items-center justify-between'
    ),
    html.div(
      attr.class('flex items-center gap-4'),
      html.span(
        attr.class('text-sm font-medium text-gray-900'),
        selectedCount.map((c) => `${c} file${c > 1 ? 's' : ''} selected`)
      ),
      html.button(
        attr.type('button'),
        attr.class('text-sm text-gray-600 hover:text-gray-900 underline'),
        on.click(onClearSelection),
        'Clear selection'
      )
    ),
    html.div(
      attr.class('flex items-center gap-2'),
      Button(
        {
          variant: 'ghost' as ButtonVariant,
          size: 'sm',
          disabled: performingAction,
          onClick: onMove,
        },
        Icon({ icon: 'mdi:folder-move' }, attr.class('w-4 h-4 mr-2')),
        'Move to Folder'
      ),
      Button(
        {
          variant: 'ghost' as ButtonVariant,
          size: 'sm',
          disabled: performingAction,
          onClick: onCopy,
        },
        Icon({ icon: 'mdi:content-copy' }, attr.class('w-4 h-4 mr-2')),
        'Copy to Folder'
      ),
      ConfirmDialog(
        {
          title: 'Delete Files',
          message: selectedCount.map(
            (c) =>
              `Are you sure you want to delete ${c} file(s)? This action cannot be undone.`
          ),
          confirmText: 'Delete',
          variant: 'danger',
          onConfirm: onDelete,
        },
        html.button(
          attr.type('button'),
          attr.class(
            'inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          ),
          attr.disabled(performingAction),
          Icon(
            {
              icon: performingAction.map((p): string =>
                p ? 'mdi:loading' : 'mdi:delete'
              ),
            },
            attr.class(
              performingAction.map((p) =>
                p
                  ? 'w-4 h-4 mr-2 animate-spin text-red-600'
                  : 'w-4 h-4 mr-2 text-red-600'
              )
            )
          ),
          html.span(
            attr.class('text-red-600'),
            performingAction.map((p) => (p ? 'Deleting...' : 'Delete'))
          )
        )
      )
    )
  )
}
